import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import MotionSection from '../components/ui/MotionSection';
import Spotlight from '../components/ui/Spotlight';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiTarget, FiEdit3, FiBarChart3, FiDownload, FiChevronDown, FiCheck } = FiIcons;

const Home = () => {
  const [demoInput, setDemoInput] = useState('');
  const [demoResults, setDemoResults] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  const generateDemo = () => {
    if (!demoInput.trim()) return;

    const samples = [
      "🚀 Quick question about [Company]'s growth",
      "💡 5-minute solution for [Pain Point]",
      "📈 [Company] + [Your Product] = 30% more leads?"
    ];
    setDemoResults(samples);
  };

  const features = [
    {
      icon: FiTarget,
      title: "Smart Personalization",
      description: "Dynamic variables and AI-powered customization for each prospect."
    },
    {
      icon: FiEdit3,
      title: "Tones & Frameworks",
      description: "AIDA, PAS, Before-After-Bridge and multiple writing styles."
    },
    {
      icon: FiBarChart3,
      title: "A/B Testing",
      description: "Generate multiple variants and test what converts best."
    },
    {
      icon: FiDownload,
      title: "Export & CRM-ready",
      description: "Copy, save, or export to CSV/PDF for your outreach tools."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Input",
      description: "Describe your offer and target audience"
    },
    {
      number: "02",
      title: "Generate",
      description: "AI creates multiple email variants instantly"
    },
    {
      number: "03",
      title: "Send/Export",
      description: "Copy, save, or export to your CRM"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Sales Director",
      company: "TechFlow",
      content: "Increased our cold email response rate by 340% in just 2 weeks."
    },
    {
      name: "Mike Rodriguez",
      role: "Founder",
      company: "GrowthLab",
      content: "Cut our email writing time from hours to minutes. Game changer!"
    }
  ];

  const faqs = [
    {
      question: "How does the AI generate emails?",
      answer: "Our AI uses advanced language models trained on high-converting cold email patterns, frameworks like AIDA and PAS, and personalization techniques."
    },
    {
      question: "Can I customize the tone and style?",
      answer: "Yes! Choose from multiple tones (Friendly, Formal, Bold, etc.) and frameworks (AIDA, PAS, BAB) to match your brand voice."
    },
    {
      question: "What's included in the free plan?",
      answer: "Free plan includes 10 generations per day, 3 email variants, and the ability to save up to 20 emails."
    },
    {
      question: "How do I export emails to my CRM?",
      answer: "Pro users can export emails as CSV or PDF files, or copy them directly to paste into any CRM or email tool."
    },
    {
      question: "Is there a refund policy?",
      answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied, we'll refund your subscription."
    },
    {
      question: "Can I cancel anytime?",
      answer: "Absolutely! You can cancel your subscription at any time from your dashboard. No long-term commitments."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <MotionSection className="relative overflow-hidden py-20 lg:py-32">
        <Spotlight size={1000} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6"
          >
            Write high-converting cold emails<br />
            <span className="gradient-text">in seconds with AI</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl lg:text-2xl text-light/80 mb-8 max-w-3xl mx-auto"
          >
            Personalize, A/B test, and export outreach emails—without the blank page.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <Link
              to="/app"
              className="btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg"
            >
              Try Free
            </Link>
            <Link
              to="/watch-demo"
              className="flex items-center space-x-2 text-light/80 hover:text-primary transition-colors"
            >
              <SafeIcon icon={FiPlay} className="w-5 h-5" />
              <span>Watch Demo</span>
            </Link>
          </motion.div>

          {/* Live Demo Strip */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="max-w-2xl mx-auto"
          >
            <div className="gradient-border rounded-lg p-6">
              <div className="gradient-border-inner p-6">
                <h3 className="text-lg font-semibold mb-4">Try it now:</h3>
                <div className="flex gap-3 mb-4">
                  <input
                    type="text"
                    placeholder="Your niche/offer (e.g., 'CRM software for real estate')"
                    value={demoInput}
                    onChange={(e) => setDemoInput(e.target.value)}
                    className="flex-1 px-4 py-3 bg-dark border border-primary/20 rounded-lg focus:outline-none focus:border-primary"
                  />
                  <button
                    onClick={generateDemo}
                    className="btn-primary px-6 py-3 rounded-lg text-white font-medium"
                  >
                    Generate
                  </button>
                </div>
                {demoResults.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="space-y-2"
                  >
                    {demoResults.map((result, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-left"
                      >
                        {result}
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </MotionSection>

      {/* Features Section */}
      <MotionSection id="features" className="py-20 relative">
        <Spotlight size={600} color="rgba(4,92,67,0.1)" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Everything you need to <span className="gradient-text">convert prospects</span>
            </h2>
            <p className="text-xl text-light/80 max-w-3xl mx-auto">
              From personalization to A/B testing, we've got every aspect of cold email covered.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="gradient-border rounded-xl p-6 hover:scale-105 transition-transform duration-300"
              >
                <div className="gradient-border-inner p-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-light/70">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </MotionSection>

      {/* How It Works */}
      <MotionSection className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              How it <span className="gradient-text">works</span>
            </h2>
            <p className="text-xl text-light/80">Simple, fast, effective</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{step.number}</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                <p className="text-light/70">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </MotionSection>

      {/* Social Proof */}
      <MotionSection className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-12">
              Trusted by <span className="gradient-text">sales teams</span>
            </h2>
            {/* Logos */}
            <div className="flex justify-center items-center space-x-12 mb-16 opacity-60">
              <div className="text-2xl font-bold">TechFlow</div>
              <div className="text-2xl font-bold">GrowthLab</div>
              <div className="text-2xl font-bold">SalesForce</div>
            </div>
            {/* Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="gradient-border rounded-xl p-6"
                >
                  <div className="gradient-border-inner p-6">
                    <p className="text-lg mb-6 italic">"{testimonial.content}"</p>
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-light/60">{testimonial.role} at {testimonial.company}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </MotionSection>

      {/* Pricing Teaser */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-12">
            Simple <span className="gradient-text">pricing</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-primary/20 rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-4">Free</h3>
              <p className="text-3xl font-bold mb-6">$0<span className="text-lg font-normal text-light/60">/month</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                  <span>10 generations/day</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                  <span>3 email variants</span>
                </li>
                <li className="flex items-center space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                  <span>Save up to 20 emails</span>
                </li>
              </ul>
              <Link
                to="/app"
                className="block w-full py-3 border border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-white transition-colors"
              >
                Start Free
              </Link>
            </div>
            <div className="gradient-border rounded-xl p-8 relative">
              <div className="gradient-border-inner p-8">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Pro</h3>
                <p className="text-3xl font-bold mb-6">$12<span className="text-lg font-normal text-light/60">/month</span></p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                    <span>Unlimited generations</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                    <span>A/B testing variants</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                    <span>PDF/CSV export</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary" />
                    <span>Projects & history</span>
                  </li>
                </ul>
                <Link
                  to="/pricing"
                  className="block w-full btn-primary py-3 rounded-lg text-white font-medium"
                >
                  Go Pro
                </Link>
              </div>
            </div>
          </div>
        </div>
      </MotionSection>

      {/* FAQ */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Frequently <span className="gradient-text">asked questions</span>
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="border border-primary/20 rounded-lg"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-primary/5 transition-colors"
                >
                  <span className="font-medium">{faq.question}</span>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`w-5 h-5 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="px-6 pb-4"
                  >
                    <p className="text-light/70">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </MotionSection>

      {/* Final CTA */}
      <MotionSection className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to <span className="gradient-text">transform</span> your outreach?
          </h2>
          <p className="text-xl text-light/80 mb-8">
            Join thousands of sales professionals who've already upgraded their cold email game.
          </p>
          <Link
            to="/app"
            className="btn-primary px-8 py-4 rounded-lg text-white font-medium text-lg inline-block"
          >
            Start Writing Better Emails
          </Link>
        </div>
      </MotionSection>
    </motion.div>
  );
};

export default Home;